// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ticksUtils } from '@shared/utils/Trading/TicksUtils';
import { OffsetModeViewEnum } from './OffsetModeViewEnum';
import { SlTpPriceType } from '../Enums/Constants';
import { formatTruncatePriceByVariableTickSize } from '../Formatters/PriceFormatter';
import { Intervals } from '../Instruments/Intervals';
import { MathUtils } from '../MathUtils';
import { NumericUtils } from '../NumericUtils';
class _SlTpUtils {
    toVisualValue(realValue, instrument, visualOffset) {
        if (isNaN(realValue) || isNullOrUndefined(instrument)) {
            return realValue;
        }
        switch (visualOffset) {
            case OffsetModeViewEnum.Ticks:
                return MathUtils.Round(realValue, 0);
            case OffsetModeViewEnum.TicksFractionalForForex:
                if (ticksUtils.AllowUseFractinalTicksForForex(instrument.getType())) {
                    return MathUtils.Round(realValue * 0.1, 1);
                }
                else {
                    return MathUtils.Round(realValue, 0);
                }
            case OffsetModeViewEnum.Points:
                return MathUtils.Round(realValue * instrument.getDefaultPointSize(), instrument.getDefaultPrecision());
            case OffsetModeViewEnum.Percent:
                return MathUtils.Round(realValue, 2);
            default:
                return realValue;
        }
    }
    toRealValue(visualValue, instrument, viewMode) {
        if (isNaN(visualValue) || isNullOrUndefined(instrument)) {
            return visualValue;
        }
        switch (viewMode) {
            case OffsetModeViewEnum.Ticks:
                return MathUtils.Round(visualValue, 0);
            case OffsetModeViewEnum.TicksFractionalForForex:
                if (ticksUtils.AllowUseFractinalTicksForForex(instrument.getType())) {
                    return MathUtils.Round(visualValue * 10, 0);
                }
                else {
                    return MathUtils.Round(visualValue, 0);
                }
            case OffsetModeViewEnum.Points:
                return MathUtils.Round(visualValue / instrument.getDefaultPointSize(), 0);
            case OffsetModeViewEnum.Percent:
                return MathUtils.Round(visualValue, 2);
            default:
                return visualValue;
        }
    }
    calculatePercent(basePrice, sltpPrice) {
        return MathUtils.Round((Math.abs(basePrice - sltpPrice) / basePrice) * 100, 2);
    }
    precentToTicks(isBuy, isSl, basePrice, precent, instrument, offsetType) {
        const ticks = ticksUtils.CalculateTicks(basePrice, basePrice * ((precent * this.getDirectionSign(isBuy, isSl)) / 100), instrument.getVariableTickList(), false, offsetType);
        return MathUtils.Round(ticks, 0);
    }
    calculateTicks(basePrice, sltpPrice, instrument, offsetType) {
        const ticks = ticksUtils.CalculateTicks(basePrice, sltpPrice - basePrice, instrument.getVariableTickList(), false, offsetType);
        return MathUtils.Round(Math.abs(ticks), 0);
    }
    getSlTpNumericParameters(parameters) {
        const instrument = parameters.instrument;
        const offsetType = parameters.offsetType;
        if (isNullOrUndefined(instrument)) {
            return { min: 0, max: 0, step: 0, precision: 0 };
        }
        if (isNullOrUndefined(offsetType)) {
            return NumericUtils.getPriceNumericParameters(instrument);
        }
        const max = NumericUtils.MAXVALUE;
        const precision = instrument.getDefaultPrecision();
        const step = instrument.getDefaultPointSize();
        const intervals = Intervals.GetIntervalsFromInstrument(instrument, max);
        switch (offsetType) {
            case OffsetModeViewEnum.Ticks:
                return { min: 1, max, step: 1, precision: 0 };
            case OffsetModeViewEnum.TicksFractionalForForex:
                if (ticksUtils.AllowUseFractinalTicksForForex(instrument.getType())) {
                    return { min: 0.1, max, step: 0.1, precision: 1 };
                }
                else {
                    return { min: 1, max, step: 1, precision: 0 };
                }
            case OffsetModeViewEnum.Points:
                if (isValidArray(intervals)) {
                    return {
                        min: intervals[0].LeftValue,
                        max,
                        step: intervals[0].LeftValue,
                        precision: intervals[0].DecimalPlaces
                    };
                }
                else {
                    return { min: step, max, step, precision };
                }
            case OffsetModeViewEnum.Percent:
                return {
                    min: this.calculateMinSlTpPercent(parameters.basePrice, instrument.getVariableTickList(), parameters.isBuy, parameters.isSl),
                    max: this.calculateMaxSlTpPercent(parameters.basePrice, instrument.getVariableTickList(), parameters.isBuy, parameters.isSl),
                    step: 0.01,
                    precision: 2
                };
            default:
                return { min: 0, max: 0, step: 0, precision: 0 };
        }
    }
    getPriceDifferenceInTicks(price1, price2, instrument, offsetType) {
        if (isNullOrUndefined(instrument)) {
            return 0;
        }
        const ticks = ticksUtils.CalculateTicks(price1, price2 - price1, instrument.getVariableTickList(), false, offsetType);
        return MathUtils.Round(Math.abs(ticks), instrument.getDefaultPrecision());
    }
    getSLTPPriceType(offsetMode) {
        switch (offsetMode) {
            case OffsetModeViewEnum.Ticks:
            case OffsetModeViewEnum.TicksFractionalForForex:
            case OffsetModeViewEnum.Points:
                return SlTpPriceType.Offset;
            case OffsetModeViewEnum.Percent:
                return SlTpPriceType.Percent;
            default:
                return SlTpPriceType.Absolute;
        }
    }
    getSLTPPrice(instrument, isBuy, isSl, basePrice, sltpValue, sltpPriceType, offsetType) {
        const sign = this.getDirectionSign(isBuy, isSl);
        switch (sltpPriceType) {
            case SlTpPriceType.Absolute:
            case SlTpPriceType.ProtectiveAbsolute:
                return sltpValue;
            case SlTpPriceType.Offset:
            case SlTpPriceType.TrOffset:
                return ticksUtils.CalculatePrice(basePrice, sign * sltpValue, instrument.getVariableTickList(), false, offsetType);
            case SlTpPriceType.Percent:
                return ticksUtils.CalculatePrice(basePrice, sign * this.precentToTicks(isBuy, isSl, basePrice, sltpValue, instrument, offsetType), instrument.getVariableTickList(), false, offsetType);
            default:
                return NaN;
        }
    }
    getSLLPrice(instrument, isBuy, basePrice, slValue, sllValue, slPriceType, offsetType) {
        if (!isValidNumber(sllValue)) {
            return 0;
        }
        const sign = this.getDirectionSign(isBuy, true);
        const sllOffset = slValue * sign + sllValue * sign;
        switch (slPriceType) {
            case SlTpPriceType.Absolute:
            case SlTpPriceType.ProtectiveAbsolute:
                return sllValue;
            case SlTpPriceType.Offset: {
                return ticksUtils.CalculatePrice(basePrice, sllOffset, instrument.getVariableTickList(), false, offsetType);
            }
            default:
                return NaN;
        }
    }
    // returns offset in Ticks or Percent
    getSLTPOffset(instrument, basePrice, sltpValue, sltpPriceType, offsetType) {
        switch (sltpPriceType) {
            case SlTpPriceType.Absolute:
            case SlTpPriceType.ProtectiveAbsolute:
                return offsetType === OffsetModeViewEnum.Percent
                    ? this.calculatePercent(basePrice, sltpValue)
                    : this.calculateTicks(basePrice, sltpValue, instrument, offsetType);
            default:
                return sltpValue;
        }
    }
    getSLLOffset(instrument, slValue, sllValue, slPriceType, offsetType) {
        switch (slPriceType) {
            case SlTpPriceType.Offset:
                return sllValue;
            case SlTpPriceType.Absolute:
            case SlTpPriceType.ProtectiveAbsolute: {
                const priceDiff = slValue - sllValue;
                const sign = Math.sign(priceDiff);
                const ticks = ticksUtils.CalculateTicks(slValue, priceDiff, instrument.getVariableTickList(), false, offsetType);
                return MathUtils.Round(sign * ticks, 0);
            }
            default:
                return NaN;
        }
    }
    formatSLTPValue(sltpValue, instrument, offsetType) {
        if (!isValidNumber(sltpValue)) {
            return '';
        }
        if (isNullOrUndefined(instrument)) {
            return sltpValue.toString();
        }
        switch (offsetType) {
            case OffsetModeViewEnum.Ticks:
                return sltpValue.toFixed(0);
            case OffsetModeViewEnum.TicksFractionalForForex:
                if (ticksUtils.AllowUseFractinalTicksForForex(instrument.getType())) {
                    return sltpValue.toFixed(1);
                }
                else {
                    return sltpValue.toFixed(0);
                }
            case OffsetModeViewEnum.Points:
                return formatTruncatePriceByVariableTickSize(instrument, sltpValue);
            case OffsetModeViewEnum.Percent:
                return sltpValue.toFixed(2);
            default:
                return formatTruncatePriceByVariableTickSize(instrument, sltpValue);
        }
    }
    getDefaultVisualOffsetType(instrument, offsetType) {
        if (offsetType === OffsetModeViewEnum.TicksFractionalForForex && !isNullOrUndefined(instrument)) {
            return ticksUtils.AllowUseFractinalTicksForForex(instrument.getType())
                ? offsetType
                : OffsetModeViewEnum.Ticks;
        }
        else {
            return offsetType;
        }
    }
    getDefaultVisualOffsetTypeExcludePercent(instrument, offsetType) {
        const defaultOffsetType = this.getDefaultVisualOffsetType(instrument, offsetType);
        return defaultOffsetType === OffsetModeViewEnum.Percent ? OffsetModeViewEnum.Ticks : defaultOffsetType;
    }
    getVisualOffsetType(instrument, sltpType, offsetType) {
        const defaultVisualOffsetType = this.getDefaultVisualOffsetType(instrument, offsetType);
        switch (sltpType) {
            case SlTpPriceType.TrOffset:
                return this.getDefaultVisualOffsetTypeExcludePercent(instrument, offsetType);
            case SlTpPriceType.Offset:
                return defaultVisualOffsetType === OffsetModeViewEnum.Percent
                    ? OffsetModeViewEnum.Ticks
                    : defaultVisualOffsetType;
            case SlTpPriceType.Percent:
                return OffsetModeViewEnum.Percent;
            default:
                return null;
        }
    }
    getSLVisualOffsetType(instrument, sltpHolder, offsetType) {
        if (sltpHolder.isSlEmpty()) {
            return sltpHolder.isTpEmpty()
                ? this.getDefaultVisualOffsetType(instrument, offsetType)
                : this.getTPVisualOffsetType(instrument, sltpHolder, offsetType);
        }
        else {
            return this.getVisualOffsetType(instrument, sltpHolder.StopLossPriceType, offsetType);
        }
    }
    getTPVisualOffsetType(instrument, sltpHolder, offsetType) {
        if (sltpHolder.isTpEmpty()) {
            return sltpHolder.isSlEmpty() || sltpHolder.isTrailingStop()
                ? this.getDefaultVisualOffsetType(instrument, offsetType)
                : this.getSLVisualOffsetType(instrument, sltpHolder, offsetType);
        }
        else {
            return this.getVisualOffsetType(instrument, sltpHolder.TakeProfitPriceType, offsetType);
        }
    }
    getSymbolForOffsetMode(offsetMode) {
        if (isNullOrUndefined(offsetMode)) {
            return '';
        }
        switch (offsetMode) {
            case OffsetModeViewEnum.Ticks:
                return 't';
            case OffsetModeViewEnum.TicksFractionalForForex:
                return 'f';
            case OffsetModeViewEnum.Points:
                return 'p';
            case OffsetModeViewEnum.Percent:
                return '%';
            default:
                return '';
        }
    }
    getTextForOffsetMode(offsetMode) {
        if (isNullOrUndefined(offsetMode)) {
            return '';
        }
        switch (offsetMode) {
            case OffsetModeViewEnum.Ticks:
                return 'ticks';
            case OffsetModeViewEnum.TicksFractionalForForex:
                return 'f_ticks';
            case OffsetModeViewEnum.Points:
                return 'points';
            case OffsetModeViewEnum.Percent:
                return '%';
            default:
                return '';
        }
    }
    getVisualOffsetTypeReportText(offsetMode) {
        switch (offsetMode) {
            case OffsetModeViewEnum.Ticks:
                return 'ticks';
            case OffsetModeViewEnum.Points:
                return 'points';
            case OffsetModeViewEnum.Percent:
                return '%';
            default:
                return '';
        }
    }
    getLocalizationForOffsetMode(offsetMode) {
        if (isNullOrUndefined(offsetMode)) {
            return '';
        }
        switch (offsetMode) {
            case OffsetModeViewEnum.Ticks:
            case OffsetModeViewEnum.TicksFractionalForForex:
                return 'general.trading.pips';
            case OffsetModeViewEnum.Points:
                return 'general.trading.points';
            case OffsetModeViewEnum.Percent:
                return 'general.trading.percent';
            default:
                return '';
        }
    }
    calculateMinSlTpPercent(basePrice, variableTickList, isBuy, isSl) {
        if (!isValidNumber(basePrice) || basePrice === 0) {
            return 0.01;
        }
        const sign = this.getDirectionSign(isBuy, isSl);
        const sltpPrice = ticksUtils.CalculatePrice(basePrice, sign, variableTickList, true, OffsetModeViewEnum.Percent);
        const percent = Math.abs(1 - basePrice / sltpPrice) * 100;
        return MathUtils.CeilToDecimalPlaces(percent, 2);
    }
    calculateMaxSlTpPercent(basePrice, variableTickList, isBuy, isSl) {
        if (!isValidNumber(basePrice) || basePrice === 0) {
            return NumericUtils.MAXVALUE;
        }
        const isNegativePosible = (isBuy && isSl) || (!isBuy && !isSl);
        if (!isNegativePosible) {
            return NumericUtils.MAXVALUE;
        }
        const slTpPrice = ticksUtils.CalculatePrice(0, 1, variableTickList, true, OffsetModeViewEnum.Percent);
        const percent = Math.abs(1 - slTpPrice / basePrice) * 100;
        return MathUtils.FloorToDecimalPlaces(Math.min(99.99, percent), 2);
    }
    getDirectionSign(isBuy, isSl) {
        if (isBuy) {
            return isSl ? -1 : 1;
        }
        else {
            return isSl ? 1 : -1;
        }
    }
}
export class SlTpNumericInputParameters {
    constructor(instrument, offetType, basePrice = undefined, isBuy = undefined, isSl = undefined) {
        this.instrument = instrument;
        this.offsetType = offetType;
        this.basePrice = basePrice;
        this.isBuy = isBuy;
        this.isSl = isSl;
    }
}
export const SlTpUtils = new _SlTpUtils();
